import { useEffect } from 'react';
import { Box, Collapse } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import CustomFormSelect from 'components/inputs/CustomFormSelect/CustomFormSelect';
import CustomFormTextInput from 'components/inputs/CustomFormTextInput/CustomFormTextInput';
import { toTitleCase } from 'common/utils';
import { useGetStores } from 'hooks/useStore';
import { STORE_ORDER_STATUSES, StoreOrdersFilters as Filters } from 'services/storeOrdersService';

interface Props {
  open: boolean;
  initialValues: Filters;
  loading: boolean;
  onApply: (filters: Filters) => void;
  onReset: () => void;
}

export const EMPTY_STORE_ORDERS_FILTERS: Filters = { storeId: '', status: '', from: '', to: '' };

export default function StoreOrdersFilters({ open, initialValues, loading, onApply, onReset }: Props) {
  const methods = useForm<Filters>({ defaultValues: initialValues });
  const { handleSubmit, reset, register } = methods;

  // A filter set from outside - a store's page opening this one on that store -
  // shows in the form too.
  useEffect(() => {
    reset(initialValues);
  }, [initialValues, reset]);

  // Every store fits one page of this size today; a picker that searches is the
  // next step once they do not.
  const { data: stores = [] } = useGetStores(
    { page: 1, size: 100 },
    { select: (res: any) => res.data, refetchOnWindowFocus: false },
  );

  return (
    <Collapse in={open} timeout="auto" unmountOnExit>
      <Box sx={{ px: 2, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <FormProvider {...methods}>
          <Box
            component="form"
            onSubmit={handleSubmit(onApply)}
            display="grid"
            gridTemplateColumns={{ xs: '1fr', md: 'repeat(5, 1fr)' }}
            gap={2}
          >
            <CustomFormSelect
              name="storeId"
              floatingLabel="Store"
              options={[
                { label: 'All', value: '' },
                ...(stores as any[]).map((store) => ({ label: store.info?.name ?? store.id, value: store.id })),
              ]}
            />

            <CustomFormSelect
              name="status"
              floatingLabel="Status"
              options={[
                { label: 'All', value: '' },
                ...STORE_ORDER_STATUSES.map((status) => ({ label: toTitleCase(status), value: status })),
              ]}
            />

            <CustomFormTextInput
              name="from"
              floatingLabel="From"
              type="date"
              register={register}
            />

            <CustomFormTextInput
              name="to"
              floatingLabel="To"
              type="date"
              register={register}
            />

            <Box display="flex" gap={1} alignItems="flex-end" justifyContent="flex-end" sx={{ alignSelf: 'end' }}>
              <CustomButton
                fullWidth
                text="Reset"
                variant="outlined"
                height={40}
                disabled={loading}
                onClick={() => {
                  reset(EMPTY_STORE_ORDERS_FILTERS);
                  onReset();
                }}
              />
              <CustomButton
                fullWidth
                text="Search"
                type="submit"
                height={40}
                loading={loading}
              />
            </Box>
          </Box>
        </FormProvider>
      </Box>
    </Collapse>
  );
}
